/**
 * Skills data for the technical skills journey page
 * Extracted from technical-skills-journey page
 */

export const skillsData = [
  // Frontend Skills
  {
    id: 1,
    name: "React",
    category: "Frontend",
    proficiency: "Expert",
    experience: 4,
    projects: 25,
    lastUsed: "Current",
    color: "bg-blue-500",
    icon: "Code2",
    isLearning: false,
    description: `React is my primary frontend framework, where I've built everything from simple landing pages to complex enterprise applications. I'm proficient in React 18 features including concurrent rendering, Suspense, and the latest hooks patterns.`,
    notableProjects: [
      {
        name: "E-Commerce Platform",
        description: "Full-featured e-commerce solution with real-time inventory management",
        year: "2024",
        role: "Lead Frontend Developer",
        impact: "50K+ active users"
      },
      {
        name: "Analytics Dashboard",
        description: "Real-time data visualization platform for business intelligence",
        year: "2023",
        role: "Frontend Architect",
        impact: "Reduced load time by 60%"
      }
    ],
    certifications: [
      {
        name: "React Developer Certification",
        issuer: "Meta",
        year: "2023"
      }
    ],
    learningResources: [
      {
        title: "React 18 Advanced Patterns",
        type: "Course",
        status: "Completed"
      }
    ]
  },
  {
    id: 2,
    name: "TypeScript",
    category: "Frontend",
    proficiency: "Advanced",
    experience: 3,
    projects: 18,
    lastUsed: "Current",
    color: "bg-blue-600",
    icon: "FileCode",
    isLearning: false,
    description: `TypeScript has become essential in my development workflow, providing type safety and better developer experience. I use it for all new projects and have migrated several legacy JavaScript codebases.`,
    notableProjects: [
      {
        name: "Component Library",
        description: "Reusable UI component library with full TypeScript support",
        year: "2023",
        role: "Library Architect",
        impact: "Used by 10+ teams"
      }
    ],
    certifications: [],
    learningResources: []
  },
  {
    id: 3,
    name: "Vue.js",
    category: "Frontend",
    proficiency: "Intermediate",
    experience: 2,
    projects: 8,
    lastUsed: "2023",
    color: "bg-green-500",
    icon: "Layers",
    isLearning: false,
    description: `Vue.js is my secondary frontend framework, which I've used for several client projects. I appreciate its gentle learning curve and excellent documentation.`,
    notableProjects: [
      {
        name: "Content Management System",
        description: "Custom CMS built with Vue.js and Nuxt.js",
        year: "2023",
        role: "Full-stack Developer",
        impact: "Improved content workflow by 40%"
      }
    ],
    certifications: [],
    learningResources: []
  },

  // Backend Skills
  {
    id: 4,
    name: "Node.js",
    category: "Backend",
    proficiency: "Advanced",
    experience: 4,
    projects: 22,
    lastUsed: "Current",
    color: "bg-green-600",
    icon: "Server",
    isLearning: false,
    description: `Node.js is my go-to backend technology for building scalable APIs and microservices. I'm experienced with Express.js, Fastify, and building real-time applications with Socket.io.`,
    notableProjects: [
      {
        name: "Real-time Chat Platform",
        description: "Scalable chat application supporting 10K+ concurrent users",
        year: "2024",
        role: "Backend Lead",
        impact: "99.9% uptime"
      }
    ],
    certifications: [],
    learningResources: []
  },
  {
    id: 5,
    name: "Python",
    category: "Backend",
    proficiency: "Intermediate",
    experience: 2,
    projects: 12,
    lastUsed: "2024",
    color: "bg-yellow-600",
    icon: "Code",
    isLearning: true,
    learningGoal: "Master Django and FastAPI for web development",
    learningProgress: 70,
    description: `Python is my language of choice for data processing, automation scripts, and increasingly for web development. Currently expanding my skills in Django and FastAPI.`,
    notableProjects: [
      {
        name: "Data Processing Pipeline",
        description: "Automated data processing system for analytics platform",
        year: "2023",
        role: "Backend Developer",
        impact: "Processed 1M+ records daily"
      }
    ],
    certifications: [],
    learningResources: [
      {
        title: "Django for Professionals",
        type: "Book",
        status: "In Progress"
      }
    ]
  },
  {
    id: 6,
    name: "PostgreSQL",
    category: "Database",
    proficiency: "Advanced",
    experience: 3,
    projects: 15,
    lastUsed: "Current",
    color: "bg-blue-700",
    icon: "Database",
    isLearning: false,
    description: `PostgreSQL is my preferred relational database for complex applications requiring ACID compliance and advanced querying capabilities. Experienced with performance optimization and complex queries.`,
    notableProjects: [
      {
        name: "Financial Management System",
        description: "Complex financial data management with advanced reporting",
        year: "2024",
        role: "Database Architect",
        impact: "Sub-second query performance"
      }
    ],
    certifications: [],
    learningResources: []
  },

  // DevOps Skills
  {
    id: 7,
    name: "AWS",
    category: "DevOps",
    proficiency: "Advanced",
    experience: 3,
    projects: 16,
    lastUsed: "Current",
    color: "bg-orange-500",
    icon: "Cloud",
    isLearning: false,
    description: `AWS is my primary cloud platform for deploying and scaling applications. Experienced with EC2, S3, Lambda, RDS, and CloudFormation for infrastructure as code.`,
    notableProjects: [
      {
        name: "Serverless API Platform",
        description: "Fully serverless API using Lambda and API Gateway",
        year: "2024",
        role: "Cloud Architect",
        impact: "99.99% availability"
      }
    ],
    certifications: [
      {
        name: "AWS Solutions Architect Associate",
        issuer: "Amazon Web Services",
        year: "2023"
      }
    ],
    learningResources: []
  },
  {
    id: 8,
    name: "Docker",
    category: "DevOps",
    proficiency: "Advanced",
    experience: 3,
    projects: 20,
    lastUsed: "Current",
    color: "bg-blue-600",
    icon: "Package",
    isLearning: false,
    description: `Docker is essential in my development workflow for creating consistent environments across development, testing, and production. Experienced with multi-stage builds and Docker Compose.`,
    notableProjects: [
      {
        name: "Microservices Architecture",
        description: "Containerized microservices with Docker and Kubernetes",
        year: "2023",
        role: "DevOps Engineer",
        impact: "Reduced deployment time by 80%"
      }
    ],
    certifications: [],
    learningResources: []
  },

  // Tools & Others
  {
    id: 9,
    name: "Git",
    category: "Tools",
    proficiency: "Expert",
    experience: 5,
    projects: 50,
    lastUsed: "Current",
    color: "bg-gray-600",
    icon: "GitBranch",
    isLearning: false,
    description: `Git is fundamental to my development workflow. I'm experienced with complex branching strategies, conflict resolution, and have mentored teams on Git best practices.`,
    notableProjects: [],
    certifications: [],
    learningResources: []
  },
  {
    id: 10,
    name: "Jest",
    category: "Testing",
    proficiency: "Advanced",
    experience: 3,
    projects: 18,
    lastUsed: "Current",
    color: "bg-red-600",
    icon: "TestTube",
    isLearning: false,
    description: `Jest is my primary testing framework for JavaScript applications. I write comprehensive unit tests, integration tests, and have set up automated testing pipelines.`,
    notableProjects: [
      {
        name: "Testing Framework Setup",
        description: "Comprehensive testing setup for React applications",
        year: "2023",
        role: "QA Lead",
        impact: "95% code coverage"
      }
    ],
    certifications: [],
    learningResources: []
  }
];

export const skillCategories = ['All', 'Frontend', 'Backend', 'Database', 'DevOps', 'Tools', 'Testing'];

export default skillsData;
